import React, { useEffect, useMemo } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getDateFnsLocale } from "../../../utils/dateFnsLocale";
import styles from "./DateRange.module.css";
import {
    startOfDay,
    maxEndDateForAnalyticsStyleRange,
    minStartDateForAnalyticsStyleRange,
} from "../utils/deriveDateBuckets";

// Matches the MultiSelect style tokens used across the analytics page so both
// inputs sit flush next to each other (same height, border colour and radius).
const analyticsPickerTheme = createTheme({
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    minHeight: 42,
                    fontSize: 13,
                    borderRadius: 11,
                    color: "#0f2a2e",
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e1e8ea",
                        borderRadius: 11,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--green-600)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--green-600)",
                        borderWidth: 1,
                    },
                    "& input": {
                        padding: "4px 8px",
                        fontSize: 13,
                        color: "#0f2a2e",
                    },
                    "& input::placeholder": {
                        color: "#6b7e84",
                        opacity: 1,
                        fontSize: 13,
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: { fontSize: 13 },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: "#6b7e84",
                    "&:hover": { color: "var(--green-600)" },
                },
            },
        },
    },
});

interface DateRangeProps {
    start: Date | null;
    end: Date | null;
    onStartChange: (d: Date | null) => void;
    onEndChange: (d: Date | null) => void;
    /**
     * Max number of days between start and end. When set, both pickers
     * constrain their min/max so the user can never form a window the
     * server would reject.
     */
    maxRangeDays?: number;
}

const DateRange: React.FC<DateRangeProps> = ({ start, end, onStartChange, onEndChange, maxRangeDays }) => {
    const { t, i18n } = useTranslation();
    const tt = t as unknown as (k: string, opts?: Record<string, unknown>) => string;

    const adapterLocale = useMemo(() => getDateFnsLocale(i18n.language), [i18n.language]);

    const maxEnd = useMemo(
        () => (maxRangeDays && start ? maxEndDateForAnalyticsStyleRange(start, maxRangeDays) : undefined),
        [maxRangeDays, start],
    );
    const minStart = useMemo(
        () => (maxRangeDays && end ? minStartDateForAnalyticsStyleRange(end, maxRangeDays) : undefined),
        [maxRangeDays, end],
    );

    useEffect(() => {
        if (!maxRangeDays || !start || !end || !maxEnd) return;
        if (startOfDay(end).getTime() > startOfDay(maxEnd).getTime()) {
            onEndChange(maxEnd);
        }
    }, [maxRangeDays, start, end, maxEnd, onEndChange]);

    useEffect(() => {
        if (!maxRangeDays || !start || !end || !minStart) return;
        if (startOfDay(start).getTime() < startOfDay(minStart).getTime()) {
            onStartChange(minStart);
        }
    }, [maxRangeDays, start, end, minStart, onStartChange]);

    return (
        <ThemeProvider theme={analyticsPickerTheme}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
                <div className={styles.root}>
                    <DatePicker
                        value={start}
                        onChange={(v: Date | null) => onStartChange(v)}
                        minDate={minStart}
                        maxDate={end ?? undefined}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                fullWidth: true,
                                size: "small",
                                placeholder: tt("startDate"),
                            } as any,
                        }}
                    />
                    <DatePicker
                        value={end}
                        onChange={(v: Date | null) => onEndChange(v)}
                        minDate={start ?? undefined}
                        maxDate={maxEnd}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                fullWidth: true,
                                size: "small",
                                placeholder: tt("endDate"),
                            } as any,
                        }}
                    />
                    {maxRangeDays ? (
                        <p className={styles.hint}>{tt("dateRangeMaxDays", { days: maxRangeDays })}</p>
                    ) : null}
                </div>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default DateRange;
